/**
 * Status badges for a Marital Balance Sheet account row.
 *
 * - "Suspected": Livi proposed this account from transfers / check payees to an
 *   account the case holds no statements for, and the user confirmed adding it.
 *   It resolves into the real account automatically once statements are scanned.
 * - "No statements yet": a hand-entered account of a kind that normally comes with
 *   statements (bank, investment, card, loan) that nothing scanned backs yet — the
 *   practitioner's "still need to request these" checklist.
 */

// MBS categories whose accounts normally come with statements.
export const STATEMENT_BACKED_CATEGORIES = new Set([
  'Cash', 'Investments', 'Credit Card Debt', 'Mortgage Debt', 'Personal Loans',
  'Vehicle Loans', 'Student Loans', 'Business Debt',
]);

// Subcategories inside those categories that never have statements.
const NEVER_STATEMENT_SUBCATEGORIES = new Set(['Cash on Hand']);

export const ACCOUNT_STATUS_SUSPECTED = 'suspected';

export function isSuspectedAccount(account) {
  return account?._status === ACCOUNT_STATUS_SUSPECTED;
}

export function needsStatements(account, categoryName) {
  if (!account || account._editable !== true) return false; // scanned accounts have their statements
  if (!STATEMENT_BACKED_CATEGORIES.has(categoryName)) return false;
  if (NEVER_STATEMENT_SUBCATEGORIES.has(account.subcategory)) return false;
  return true;
}

/**
 * @returns {{ label: string, variant: 'warning' | 'info', title: string }[]}
 */
export function getAccountStatusBadges(account, categoryName) {
  const badges = [];
  if (isSuspectedAccount(account)) {
    badges.push({
      label: 'Suspected',
      variant: 'warning',
      title: 'Proposed by Livi from transfers to an account with no statements on file. '
        + 'It resolves into the real account once its statements are scanned.',
    });
  }
  if (needsStatements(account, categoryName)) {
    badges.push({
      label: 'No statements yet',
      variant: 'info',
      title: 'Entered by hand; no scanned statements back this account yet.',
    });
  }
  return badges;
}
